import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import Badge from '@/components/Badge/Badge'

import ConfirmPopup from '../../../../../../components/Popups/ConfirmPopup'
import CheckboxField from '../../../../../../components/FormLegacy/FormFieldsV2/CheckboxField'
import Icon from '../../../../../../components/Icons'
import API from '../../../../../../api/api'
import { getChecklistTypeOptions } from '../../../helpers'

import RevisionSummaryWindow from './Components/RevisionSummaryWindow'
import ChecklistDetailsBody from './Components/ChecklistDetailsBody'
import ChecklistPreviewNavbar from './Components/ChecklistPreviewNavbar'

const ChecklistPreview = ({ data, onRemoved }) => {
  const { t } = useTranslation(['checklists-dashboard', 'common'])
  const checklistTypeOptions = getChecklistTypeOptions(t)
  const { inspectionId } = useParams()
  const navigate = useNavigate()
  const selectedBuilding = useSelector(selectActiveBuilding)
  const buildingTimezone = selectedBuilding?.timezone

  const [selectedChecklist, setSelectedChecklist] = useState(data || null)
  const [editData, setEditData] = useState(data || null)
  const [isSubmitting, setSubmitting] = useState(false)
  const [selectedRoom, selectRoom] = useState(null)
  const [errors, setErrors] = useState([])
  const [approveWindowOpen, setApproveWindowOpen] = useState(false)
  const [revisionWindowOpen, setRevisionWindowOpen] = useState(false)
  const [revisionSummaryWindowOpen, setRevisionSummaryWindowOpen] = useState(false)
  const [revisionMode, setRevisionMode] = useState(false)
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false)

  const fetchDetails = () => {
    setSubmitting(true)
    API.getChecklistInspectionDetails(inspectionId)
      .then((response) => {
        const checklist = response?.data || response
        if (checklist) {
          setSelectedChecklist(checklist)
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  useEffect(() => {
    if (!selectedChecklist && inspectionId) {
      fetchDetails()
    }
  }, [inspectionId, selectedChecklist])

  useEffect(() => {
    if (!selectedChecklist) {
      return
    }

    setEditData(selectedChecklist)
    selectRoom(selectedChecklist.rooms.length ? selectedChecklist.rooms[0] : null)

    const newErrors = []
    if (selectedChecklist.status === 'Pending Approval') {
      selectedChecklist.rooms.forEach((room) => {
        if (room.roomItems.some((item) => item.hasIssue)) newErrors.push(`roomId-${room.id}`)
      })
    }
    setErrors(newErrors)
  }, [selectedChecklist])

  const checklistType =
    selectedChecklist &&
    checklistTypeOptions.find((opt) => opt.value === selectedChecklist.type)?.label
  const isGuided = !!selectedChecklist?.isGuided

  const handleApproveChecklist = () => {
    if (!selectedChecklist) return

    setSubmitting(true)
    const updatedChecklist = { ...selectedChecklist, status: 'Approved' }

    API.updateChecklist(updatedChecklist)
      .then(() => {
        setSelectedChecklist(updatedChecklist)
        setEditData(updatedChecklist)
      })
      .finally(() => {
        setSubmitting(false)
        setApproveWindowOpen(false)
      })
  }

  const revisionConfirmInfo = () => (
    <div>
      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 20 }}>
        {t('checklists-dashboard:preview.revisionInstructions')}
      </div>
      <div
        style={{
          fontWeight: 600,
          fontSize: 14,
          color: '#5C5C5C',
          display: 'flex',
          gap: 7,
          marginBottom: 4,
          fill: '#5C5C5C',
        }}
      >
        <Icon icon="exclamationPoint" />
        {t('checklists-dashboard:preview.importantInformation')}
      </div>
      <div style={{ fontWeight: 400, fontSize: 14, color: '#5C5C5C' }}>
        {t('checklists-dashboard:preview.revisionRules')}
      </div>
    </div>
  )

  const changeRoomStatusOfRevision = (room) => {
    if (!editData) return

    setEditData((prevEditData) => ({
      ...prevEditData,
      rooms: prevEditData.rooms.map((r) => {
        if (r.id !== room.id) {
          return r
        }
        return {
          ...r,
          revisionRequested: !room.revisionRequested,
          roomItems: r.roomItems.map((roomItem) => {
            return {
              ...roomItem,
              revisionRequested: !room.revisionRequested,
            }
          }),
        }
      }),
    }))
  }

  const updateSelectedRoom = (room) => {
    const selectedFullRoom = room.roomItems.every((roomItem) => roomItem.revisionRequested)
    if (!editData) return

    setEditData((prevEditData) => ({
      ...prevEditData,
      rooms: prevEditData.rooms.map((r) => {
        if (r.id !== room.id) {
          return r
        }
        return {
          ...room,
          revisionRequested: selectedFullRoom || false,
        }
      }),
    }))
  }

  const onHandleSelectRoom = (e, room) => {
    if (!['LABEL', 'INPUT'].includes(e.target.nodeName)) {
      selectRoom(room)
    }
  }

  const onHandleCancelRevisionMode = () => {
    if (!selectedChecklist || !selectedRoom) return

    setEditData(selectedChecklist)
    setRevisionMode(false)
    selectRoom(selectedChecklist.rooms.find((room) => room.id === selectedRoom?.id))
  }

  const handleUpdateChecklist = (updatedChecklist) => {
    setSubmitting(true)

    return API.updateChecklist(updatedChecklist)
      .then(() => {
        setSelectedChecklist(updatedChecklist)
        setEditData(updatedChecklist)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleRemoveChecklist = () => {
    setRemoveConfirmOpen(true)
  }

  const handleConfirmRemoveChecklist = () => {
    setRemoveConfirmOpen(false)
    setSubmitting(true)
    API.removeChecklist(editData?.id)
      .then(() => {
        if (onRemoved) {
          onRemoved()
        } else {
          navigate('/dashboard/building-dashboard/checklists')
        }
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  if (!editData || !selectedRoom) {
    return <div className={`ctp-checklist-preview ${isSubmitting ? 'is-loading' : ''}`} />
  }

  return (
    <div className={`ctp-checklist-preview ${isSubmitting ? 'is-loading' : ''}`}>
      <ChecklistPreviewNavbar
        data={editData}
        isSubmitting={isSubmitting}
        timezone={buildingTimezone || editData?.building?.timezone}
        revisionMode={revisionMode}
        onHandleApprove={() => setApproveWindowOpen(true)}
        onRequestRevision={() => setRevisionWindowOpen(true)}
        onCancelRevision={onHandleCancelRevisionMode}
        onSubmitRevision={() => setRevisionSummaryWindowOpen(true)}
        removeChecklist={handleRemoveChecklist}
      />
      <div className="template-body-container">
        <div className="side-navigation">
          {editData.rooms.map((room) => {
            const poorRoomItems = room.roomItems.filter((item) => item.rating === 'Poor')
            const hasIssueRoomItems = room.roomItems.filter((item) => item.hasIssue)
            const showPoorBadge = poorRoomItems.length > 0 || hasIssueRoomItems.length > 0

            return (
              <div
                key={room.id}
                className={`nav-element ${selectedRoom.id === room.id ? 'selected' : ''}`}
                onClick={(e) => onHandleSelectRoom(e, room)}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flexStart',
                    width: 'calc(100% - 75px)',
                  }}
                >
                  <div className="element-title">{room.name}</div>
                  <div className="element-items-count">
                    {t('checklists-dashboard:preview.itemsCount', {
                      num: room.roomItems.length,
                    })}
                  </div>
                </div>
                <div className="room-action-elements">
                  {showPoorBadge && (
                    <Badge color="red-filled">
                      {t('checklists-dashboard:preview.poorLabel')}{' '}
                      {poorRoomItems.length || hasIssueRoomItems.length}
                    </Badge>
                  )}
                  {errors.includes(`roomId-${room.id}`) ? (
                    <div className="element-warning">
                      <Icon icon="exclamationPoint" />
                    </div>
                  ) : null}
                  {revisionMode && (
                    <CheckboxField
                      name={`room-revision-req-id-${room.id}`}
                      onChange={() => changeRoomStatusOfRevision(room)}
                      checked={!!room.revisionRequested}
                      disabled={isSubmitting}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
        {selectedRoom && (
          <ChecklistDetailsBody
            selectedRoom={editData.rooms.find((r) => r.id === selectedRoom?.id)}
            checklistStatus={editData.status}
            isSubmitting={isSubmitting}
            revisionMode={revisionMode}
            isGuided={isGuided}
            updateSelectedRoom={updateSelectedRoom}
            checklist={selectedChecklist}
            refetchDetails={fetchDetails}
          />
        )}
      </div>
      {revisionSummaryWindowOpen && (
        <RevisionSummaryWindow
          handleCancel={() => setRevisionSummaryWindowOpen(false)}
          data={editData}
          timezone={buildingTimezone}
          updateChecklist={handleUpdateChecklist}
          setRevisionMode={setRevisionMode}
        />
      )}
      <ConfirmPopup
        title={t('checklists-dashboard:preview.confirmationTitle')}
        body={t('checklists-dashboard:preview.approveConfirmBody', { type: checklistType })}
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.confirm')}
        isOpen={approveWindowOpen}
        cancelCallback={() => setApproveWindowOpen(false)}
        confirmCallback={handleApproveChecklist}
      />
      <ConfirmPopup
        title={t('checklists-dashboard:preview.confirmationTitle')}
        body={revisionConfirmInfo()}
        cancelText={t('common:actions.cancel')}
        confirmText={t('common:actions.confirm')}
        isOpen={revisionWindowOpen}
        cancelCallback={() => setRevisionWindowOpen(false)}
        confirmCallback={() => {
          setRevisionMode(true)
          setRevisionWindowOpen(false)
        }}
      />
      <ConfirmPopup
        title={t('checklists-dashboard:preview.removeChecklistTitle')}
        body={t('checklists-dashboard:preview.removeChecklistBody')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('checklists-dashboard:preview.removeConfirmButton')}
        isOpen={removeConfirmOpen}
        cancelCallback={() => setRemoveConfirmOpen(false)}
        confirmCallback={handleConfirmRemoveChecklist}
      />
    </div>
  )
}

ChecklistPreview.propTypes = {
  data: PropTypes.object,
  onRemoved: PropTypes.func,
}

export default ChecklistPreview
